import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Company, type RyczaltEvidence } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date, money, period as writtenPeriod } from '../format';
import * as Icon from '../components/Icons';
import type { Panel } from '../components/Shell';

const currentMonth = () => new Date().toISOString().slice(0, 7);

export function RyczaltEvidencePanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo?: (panel: Panel) => void;
}) {
  const [month, setMonth] = useState(currentMonth);
  const [evidence, setEvidence] = useState<RyczaltEvidence | null>(null);
  const [notApplicable, setNotApplicable] = useState<string | null>(null);

  const load = useCallback(async () => {
    setNotApplicable(null);
    try {
      setEvidence(await api.ryczaltEvidence(company.id, month));
    } catch (failure) {
      // Firma na KPiR nie prowadzi tej ewidencji — to informacja, nie awaria
      setEvidence(null);
      setNotApplicable(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id, month]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Gdy ewidencji nie ma, nie ma też czego filtrować — wybór miesiąca znika, a zamiast niego
   * stoją dwa wyjścia: tam, gdzie księga naprawdę jest, albo tam, gdzie zmienia się formę.
   */
  if (notApplicable) {
    return (
      <>
        <div className="hd">
          <div>
            <h3>{t.ryczalt.title}</h3>
            <p>{t.ryczalt.lead}</p>
          </div>
        </div>
        <div className="panel">
          <div className="empty">
            <Icon.Book />
            <b>{t.ryczalt.otherForm}</b>
            <p>{notApplicable}</p>
            {onGo && (
              <div className="row" style={{ justifyContent: 'center' }}>
                <button type="button" className="btn sec" onClick={() => onGo('ledger')}>
                  {t.ryczalt.goToLedger}
                </button>
                <button type="button" className="btn ghost" onClick={() => onGo('settings')}>
                  {t.ryczalt.changeForm}
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.ryczalt.title}</h3>
          <p>{t.ryczalt.lead}</p>
        </div>
        <div className="sp">
          <input
            className="inp w"
            type="month"
            aria-label={t.ryczalt.month}
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </div>
      </div>

      <div className="panel">
        <header>
          <h4>{writtenPeriod(month, locale)}</h4>
          {evidence && evidence.rows.length > 0 && (
            <div className="sp">
              <span className="sm muted">
                {t.ryczalt.total}: {money(evidence.total, locale)}
              </span>
            </div>
          )}
        </header>

        {evidence && evidence.rows.length === 0 && (
          <div className="empty">
            <b>{t.ryczalt.none}</b>
          </div>
        )}

        {evidence && evidence.rows.length > 0 && (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="n">{t.ledger.ordinal}</th>
                  <th>{t.ryczalt.date}</th>
                  <th>{t.ryczalt.document}</th>
                  <th>{t.ryczalt.counterparty}</th>
                  <th className="n">{t.ryczalt.rate}</th>
                  <th className="n">{t.ryczalt.amount}</th>
                </tr>
              </thead>
              <tbody>
                {evidence.rows.map((row, index) => (
                  <tr key={`${row.documentNumber}-${row.ryczaltRate}-${index}`}>
                    <td className="n">{index + 1}</td>
                    <td className="doc">{date(row.entryDate, locale)}</td>
                    <td className="doc">{row.documentNumber ?? '—'}</td>
                    <td>{row.counterpartyName ?? '—'}</td>
                    <td className="n">{row.ryczaltRate}%</td>
                    <td className="n">{amount(row.amount, locale)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>{t.ryczalt.total}</td>
                  <td className="n">{amount(evidence.total, locale)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Podsumowanie według stawek — tak wygląda ewidencja na koniec miesiąca */}
      {evidence && evidence.rows.length > 0 && (
        <div className="panel">
          <header>
            <h4>{t.ryczalt.byRate}</h4>
          </header>
          <dl className="brk" style={{ maxWidth: 420 }}>
            {Object.entries(evidence.byRate).map(([rate, value]) => (
              <div key={rate} style={{ display: 'contents' }}>
                <dt>{rate}%</dt>
                <dd>{money(value, locale)}</dd>
              </div>
            ))}
            <dt className="tot">{t.ryczalt.total}</dt>
            <dd className="tot">{money(evidence.total, locale)}</dd>
          </dl>
        </div>
      )}
    </>
  );
}
